import {
  getProjectStatusLabel,
  getProjectTypeLabel,
  hasCustomProjectLogo,
} from "~/utils/project";
import { avatarUrl } from "~/utils/app";
import ProjectUpvoters from "./ProjectUpvoters";
import plazaStyles from "./projects.module.css";
import styles from "./ProjectPopup.module.css";
import type { PlazaProject, PromotedProject } from "~/types/project";

// 项目链接是否可跳转：只有 http(s) 网址算（填小程序名或一段文字的不能跳）
const isJumpableUrl = (url?: string | null) => /^https?:\/\//i.test(url || "");

const UpArrow = () => (
  <svg
    className={styles.upArw}
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M12 2.6c.4 0 .8.17 1.08.46l7.6 7.9c.62.64.16 1.72-.73 1.72H15.6v7.8c0 .84-.68 1.52-1.52 1.52H9.92c-.84 0-1.52-.68-1.52-1.52v-7.8H4.05c-.89 0-1.35-1.08-.73-1.72l7.6-7.9c.28-.29.68-.46 1.08-.46Z" />
  </svg>
);

const formatNum = (n?: number) =>
  n == null ? "—" : n.toLocaleString("zh-CN");

// 链接只展示协议之后的部分（设计稿为 qingchuan.app 这种裸域名写法）
const formatDisplayUrl = (url?: string) =>
  (url || "").replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");

const formatCreated = (date?: string) => {
  if (!date) return "";
  const d = new Date(date);
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}.${m}.${day} 创建`;
};

type ProjectPopupProps = {
  /** 广场列表项或推广位列表项；深链加载中还没有数据，此时为 null */
  project: PlazaProject | PromotedProject | null;
  /** 项目是否处于「最热」前三（logo 强调样式与列表保持一致） */
  top?: boolean;
  upvoted: boolean;
  upvoting: boolean;
  /** 详情加载中：先显示加载态，避免「顶一下」→「你已经顶过啦」的过渡闪烁 */
  loading?: boolean;
  onUpvote: () => void;
  onShare: () => void;
  onClose: () => void;
};

// 项目弹窗（对齐设计稿 .pop）：纯展示 + 顶一下（状态来自后端 upvoted）+ 分享
const ProjectPopup = ({
  project,
  top = false,
  upvoted,
  upvoting,
  loading = false,
  onUpvote,
  onShare,
  onClose,
}: ProjectPopupProps) => {
  if (loading || !project) {
    return (
      <div
        className={styles.mask}
        onClick={onClose}
      >
        <div className={styles.loadingBox}>加载中…</div>
      </div>
    );
  }

  const letter = (project.name || "?")[0];
  const customLogo = hasCustomProjectLogo(project.logo_url);
  const hasLink = Boolean(project.url);
  // 作者简介：优先用用户资料里的一句话简介，没填时退回项目表单里的「用户角色」
  const authorDesc = project.user?.tagline || project.role;

  return (
    <div
      className={styles.mask}
      onClick={onClose}
    >
      <div
        className={styles.pop}
        onClick={(e) => e.stopPropagation()}
      >
        <a
          className={styles.popX}
          onClick={onClose}
        >
          &times;
        </a>
        <a
          className={styles.popShare}
          onClick={onShare}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M16 6l-4-4-4 4M12 2v13" />
          </svg>
          分享
        </a>

        <div className={styles.popTop}>
          <span
            className={`${styles.popLogo} ${
              customLogo
                ? ""
                : top
                  ? styles.avatarT3
                  : styles.avatarLine
            }`}
          >
            {customLogo ? (
              <img
                className={styles.avatarImg}
                src={project.logo_url}
                alt=""
              />
            ) : (
              letter
            )}
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className={styles.popName}>
              <b>{project.name}</b>
              {project.promoting && (
                <span className={plazaStyles.flag}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 4h6l-1 6 3 3H7l3-3-1-6Z" />
                    <path d="M12 16v5" />
                  </svg>
                  付费置顶
                </span>
              )}
            </div>
            <div className={styles.popTags}>
              <span className={`${styles.popTag} ${styles.popTagOn}`}>
                {getProjectStatusLabel(project.status)}
              </span>
              {project.field_tag && (
                <span className={styles.popTag}>
                  {project.field_tag.name}
                </span>
              )}
              <span className={styles.popTag}>
                {getProjectTypeLabel(project.project_type)}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.popIntro}>{project.description}</div>

        {project.highlight && (
          <div className={styles.popHl}>{project.highlight}</div>
        )}

        {hasLink && (
          <div className={styles.popLinks}>
            {/* 推广中的项目链接可以点出去（付费权益）；非推广项目只展示不能跳 */}
            {project.promoting && isJumpableUrl(project.url) ? (
              <div className={styles.popRow}>
                <b>项目链接</b>
                <a
                  href={project.url || "#"}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>{formatDisplayUrl(project.url)}</span>
                  <svg
                    className={styles.arrR}
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 4h6v6" />
                    <path d="M20 4 12 12" />
                    <path d="M18 14v5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5H10" />
                  </svg>
                </a>
              </div>
            ) : (
              <div className={`${styles.popRow} ${styles.noJump}`}>
                <b>项目链接</b>
                <a>
                  <span>{formatDisplayUrl(project.url)}</span>
                </a>
              </div>
            )}
          </div>
        )}

        <div className={styles.popAu}>
          <span className={styles.popAuAva}>
            <img
              src={avatarUrl(project.user, 64)}
              alt=""
            />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className={styles.popAuN}>
              {project.user?.nickname || "匿名"}
            </div>
            {authorDesc && <div className={styles.popAuD}>{authorDesc}</div>}
          </div>
          <div className={styles.popAuT}>
            <span>{formatCreated(project.created_at)}</span>
            <span>{formatNum(project.views_count)} 看过</span>
          </div>
        </div>

        <div className={styles.popFoot}>
          <ProjectUpvoters
            projectId={project.id}
            count={project.upvotes_count}
          />
          <div className={styles.popCnt}>
            已有 <b>{project.upvotes_count}</b> 人顶过
          </div>
          <button
            type="button"
            className={`${styles.popUpBtn} ${
              upvoted ? styles.popUpBtnOn : styles.popUpBtnPrimary
            }`}
            disabled={upvoting}
            onClick={onUpvote}
          >
            <UpArrow />
            <span>{upvoted ? "你已经顶过啦" : upvoting ? "顶一下..." : "顶一下"}</span>
          </button>
          <div className={styles.popNote}>
            顶一下，帮项目更靠前（不消耗您的电量哦：）
            <br />
            每个项目，每个用户仅可顶一次
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectPopup;
